import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { announcementsAPI, uploadAPI, directoryAPI, adminAPI, livestreamAPI, groupsAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Shield,
  Plus,
  Bell,
  Edit,
  Trash2,
  ChevronLeft,
  Save,
  Loader2,
  Archive,
  ArchiveRestore,
  Upload,
  Image as ImageIcon,
  Video,
  X,
  Users,
  User,
  UserCog,
  ZoomIn,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Heart,
  BookOpen,
  UserX,
  UserCheck,
  ChevronRight,
  UserPlus,
  ClipboardList,
  Check,
  XCircle,
  Clock,
  Wrench,
  Crown,
  Users2,
  GitMerge,
  Gift,
  Cake
} from 'lucide-react';
import BottomNav from '../components/BottomNav';

const AdminPage = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [announcements, setAnnouncements] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingAnn, setEditingAnn] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [archiving, setArchiving] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('active');
  const [mainTab, setMainTab] = useState('announcements');
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberProfile, setMemberProfile] = useState(null);
  const [showMemberProfile, setShowMemberProfile] = useState(false);
  const [updatingRole, setUpdatingRole] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [memberFilter, setMemberFilter] = useState('all'); // all, active, inactive
  const [isLive, setIsLive] = useState(false);
  const [togglingLive, setTogglingLive] = useState(false);
  // Manage tab
  const [allGroups, setAllGroups] = useState([]);
  const [allMembers, setAllMembers] = useState([]);
  const [leaderSelections, setLeaderSelections] = useState({}); // groupId -> userId
  const [savingLeader, setSavingLeader] = useState(null);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [dupKeep, setDupKeep] = useState({}); // nameKey -> keepId
  const [merging, setMerging] = useState(null);
  const [loadingManage, setLoadingManage] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    priority: 'normal',
    imageUrl: '',
    videoUrl: ''
  });

  // New state for Create Member and Pending Requests
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [processingRequest, setProcessingRequest] = useState(null);
  const [showCreateMemberDialog, setShowCreateMemberDialog] = useState(false);
  const [creatingMember, setCreatingMember] = useState(false);
  const [fixingLeaders, setFixingLeaders] = useState(false);
  const [newMemberData, setNewMemberData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: '',
    pin: '',
    role: 'member',
    birthday: '',
    address: '',
    favoriteScripture: ''
  });
  
  // Birthday/Anniversary list state
  const [birthdayData, setBirthdayData] = useState(null);
  const [loadingBirthdays, setLoadingBirthdays] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    // Default to current month
    const months = ["January", "February", "March", "April", "May", "June",
                    "July", "August", "September", "October", "November", "December"];
    return months[new Date().getMonth()];
  });
  const [showBirthdayList, setShowBirthdayList] = useState(false);
  const [sendingCelebrationNotifications, setSendingCelebrationNotifications] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      fetchData();
      fetchPendingRequests(); // Load pending requests count on page load
    }
  }, [isAdmin]);

  const fetchData = async () => {
    try {
      const [annData, memberData] = await Promise.all([
        announcementsAPI.getAllIncludingArchived(),
        directoryAPI.getAll()
      ]);
      setAnnouncements(annData);
      setMembers(memberData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    livestreamAPI.getStatus().then(d => setIsLive(!!d.isLive)).catch(() => {});
  }, []);

  useEffect(() => {
    if (mainTab === 'manage') loadManageData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainTab]);


  const handleToggleLive = async (next) => {
    setTogglingLive(true);
    try {
      const result = await livestreamAPI.toggle(next);
      setIsLive(!!result.isLive);
    } catch (error) {
      console.error('Error toggling live status:', error);
      alert('Could not update live status. Please try again.');
    } finally {
      setTogglingLive(false);
    }
  };

  const loadManageData = async () => {
    setLoadingManage(true);
    try {
      const [groups, members, dups] = await Promise.all([
        groupsAPI.getAll(),
        directoryAPI.getAll(),
        adminAPI.getDuplicates(),
      ]);
      setAllGroups(groups || []);
      const memberList = Array.isArray(members) ? members : (members?.members || []);
      setAllMembers(memberList);
      setDuplicateGroups(dups?.duplicateGroups || []);
    } catch (error) {
      console.error('Error loading manage data:', error);
    } finally {
      setLoadingManage(false);
    }
  };

  const handleSaveLeader = async (groupId) => {
    const userId = leaderSelections[groupId];
    if (!userId) return;
    setSavingLeader(groupId);
    try {
      const result = await adminAPI.setGroupLeader(groupId, userId);
      setAllGroups(prev => prev.map(g => g.id === groupId ? { ...g, leaderId: result.leaderId, leaderName: result.leaderName } : g));
      setLeaderSelections(prev => ({ ...prev, [groupId]: '' }));
      alert(`Leader updated to ${result.leaderName}`);
    } catch (error) {
      console.error('Error setting leader:', error);
      alert(error.response?.data?.detail || 'Could not update leader.');
    } finally {
      setSavingLeader(null);
    }
  };

  const handleMergeDuplicates = async (group) => {
    const keepId = dupKeep[group.name];
    if (!keepId) { alert('Please choose which account to keep.'); return; }
    const removeIds = group.accounts.filter(a => a.id !== keepId).map(a => a.id);
    if (removeIds.length === 0) return;
    if (!window.confirm(`Merge ${removeIds.length} duplicate account(s) into the selected one? This cannot be undone.`)) return;
    setMerging(group.name);
    try {
      await adminAPI.mergeDuplicates(keepId, removeIds);
      setDuplicateGroups(prev => prev.filter(g => g.name !== group.name));
      alert('Accounts merged successfully.');
    } catch (error) {
      console.error('Error merging:', error);
      alert(error.response?.data?.detail || 'Could not merge accounts.');
    } finally {
      setMerging(null);
    }
  };


  const handleFixLeaderAccess = async () => {
    if (!window.confirm('This will fix all group leaders who cannot access their groups. Continue?')) return;
    
    setFixingLeaders(true);
    try {
      const result = await adminAPI.fixLeaderAccess();
      const fixedCount = result.fixed?.length || 0;
      const alreadyOkCount = result.alreadyOk?.length || 0;
      
      let message = `Done!\n\n`;
      if (fixedCount > 0) {
        message += `Fixed ${fixedCount} leader(s):\n`;
        result.fixed.forEach(f => {
          message += `• ${f.leaderName} - ${f.groupName}\n`;
        });
      } else {
        message += `All ${alreadyOkCount} group leaders already have proper access.`;
      }
      
      if (result.errors?.length > 0) {
        message += `\n\nIssues found (${result.errors.length}):\n`;
        result.errors.forEach(e => {
          message += `• ${e.groupName}: ${e.issue}\n`;
        });
      }
      
      alert(message);
    } catch (error) {
      console.error('Error fixing leader access:', error);
      alert('Failed to fix leader access. Please try again.');
    } finally {
      setFixingLeaders(false);
    }
  };

  const loadBirthdayData = async () => {
    setLoadingBirthdays(true);
    try {
      const data = await adminAPI.getBirthdaysAnniversaries();
      setBirthdayData(data);
      setShowBirthdayList(true);
    } catch (error) {
      console.error('Error loading birthdays/anniversaries:', error);
      alert('Failed to load birthdays and anniversaries.');
    } finally {
      setLoadingBirthdays(false);
    }
  };

  const sendCelebrationNotifications = async () => {
    setSendingCelebrationNotifications(true);
    try {
      const result = await adminAPI.sendCelebrationNotifications();
      if (result.birthdayCount === 0 && result.anniversaryCount === 0) {
        alert('No birthdays or anniversaries to celebrate today.');
      } else {
        const messages = result.notificationsSent.map(n => 
          `${n.name} (${n.type}${n.years ? ` - ${n.years} years` : ''})`
        ).join('\n');
        alert(`Celebration notifications sent!\n\n${messages}`);
      }
    } catch (error) {
      console.error('Error sending celebration notifications:', error);
      alert('Failed to send celebration notifications.');
    } finally {
      setSendingCelebrationNotifications(false);
    }
  };

  const fetchPendingRequests = async () => {
    setLoadingRequests(true);
    try {
      const data = await adminAPI.getAllJoinRequests();
      setPendingRequests(data.requests || []);
    } catch (error) {
      console.error('Error fetching pending requests:', error);
    } finally {
      setLoadingRequests(false);
    }
  };

  // Fetch pending requests when switching to that tab
  useEffect(() => {
    if (mainTab === 'requests' && pendingRequests.length === 0) {
      fetchPendingRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainTab]);

  const handleApproveRequest = async (groupId, userId) => {
    setProcessingRequest(`${groupId}-${userId}`);
    try {
      await adminAPI.handleJoinRequest(groupId, userId, 'approve');
      setPendingRequests(prev => prev.filter(r => !(r.groupId === groupId && r.userId === userId)));
      alert('Request approved successfully!');
    } catch (error) {
      console.error('Error approving request:', error);
      alert('Failed to approve request. Please try again.');
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleDenyRequest = async (groupId, userId) => {
    if (!window.confirm('Are you sure you want to deny this request?')) return;
    
    setProcessingRequest(`${groupId}-${userId}`);
    try {
      await adminAPI.handleJoinRequest(groupId, userId, 'deny');
      setPendingRequests(prev => prev.filter(r => !(r.groupId === groupId && r.userId === userId)));
      alert('Request denied.');
    } catch (error) {
      console.error('Error denying request:', error);
      alert('Failed to deny request. Please try again.');
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleCreateMember = async (e) => {
    e.preventDefault();
    
    if (!newMemberData.email || !newMemberData.password || !newMemberData.firstName || !newMemberData.lastName) {
      alert('Please fill in all required fields (Email, Password, First Name, Last Name)');
      return;
    }
    
    setCreatingMember(true);
    try {
      const result = await adminAPI.createMember(newMemberData);
      alert(`Member created successfully!\n\nEmail: ${result.email}\nName: ${newMemberData.firstName} ${newMemberData.lastName}`);
      
      // Refresh member list
      const memberData = await directoryAPI.getAll();
      setMembers(memberData);
      
      // Reset form and close dialog
      setNewMemberData({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        phone: '',
        role: 'member',
        birthday: '',
        address: '',
        favoriteScripture: ''
      });
      setShowCreateMemberDialog(false);
    } catch (error) {
      console.error('Error creating member:', error);
      const message = error.response?.data?.detail || 'Failed to create member. Please try again.';
      alert(message);
    } finally {
      setCreatingMember(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <Card className="max-w-md rounded-2xl">
          <CardContent className="p-6 text-center">
            <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-navy-900 mb-2">Access Restricted</h2>
            <p className="text-gray-600 mb-4">This page is only available to church administrators.</p>
            <Button onClick={() => navigate('/home')} className="bg-navy-900 hover:bg-navy-800 text-white rounded-xl">
              <ChevronLeft className="w-4 h-4 mr-2" />
              Go Back Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const activeAnnouncements = announcements.filter(a => !a.archived);
  const archivedAnnouncements = announcements.filter(a => a.archived);

  const handleCreate = () => {
    setFormData({ title: '', content: '', priority: 'normal', imageUrl: '', videoUrl: '' });
    setEditingAnn(null);
    setShowCreateDialog(true);
  };

  const handleEdit = (ann) => {
    setFormData({
      title: ann.title,
      content: ann.content,
      priority: ann.priority,
      imageUrl: ann.imageUrl || '',
      videoUrl: ann.videoUrl || ''
    });
    setEditingAnn(ann);
    setShowCreateDialog(true);
  };

  const handleDelete = async (annId) => {
    if (!window.confirm('Are you sure you want to permanently delete this announcement?')) return;
    
    setDeleting(annId);
    try {
      await announcementsAPI.delete(annId);
      setAnnouncements(announcements.filter(a => a.id !== annId));
    } catch (error) {
      console.error('Error deleting announcement:', error);
    } finally {
      setDeleting(null);
    }
  };

  const handleArchiveGlobal = async (annId) => {
    setArchiving(annId);
    try {
      await announcementsAPI.archiveGlobal(annId);
      setAnnouncements(announcements.map(a =>
        a.id === annId ? { ...a, archived: true } : a
      ));
    } catch (error) {
      console.error('Error archiving announcement:', error);
    } finally {
      setArchiving(null);
    }
  };

  const handleUnarchiveGlobal = async (annId) => {
    setArchiving(annId);
    try {
      await announcementsAPI.unarchiveGlobal(annId);
      setAnnouncements(announcements.map(a =>
        a.id === annId ? { ...a, archived: false } : a
      ));
    } catch (error) {
      console.error('Error unarchiving announcement:', error);
    } finally {
      setArchiving(null);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    console.log('Uploading file:', file.name, file.type, file.size);
    setUploading(true);
    try {
      const result = await uploadAPI.uploadFile(file);
      console.log('Upload result:', result);
      // Cloudinary returns full URLs, local uploads return relative URLs
      const fullUrl = result.url.startsWith('http') 
        ? result.url 
        : `${process.env.REACT_APP_BACKEND_URL}${result.url}`;
      console.log('Full URL:', fullUrl);
      
      if (result.type && result.type.startsWith('video/')) {
        setFormData(prev => ({ ...prev, videoUrl: fullUrl }));
        console.log('Set videoUrl to:', fullUrl);
      } else {
        // Images, PDFs, and other documents go to imageUrl
        setFormData(prev => ({ ...prev, imageUrl: fullUrl }));
        console.log('Set imageUrl to:', fullUrl);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Error uploading file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingAnn) {
        const updated = await announcementsAPI.update(editingAnn.id, formData);
        setAnnouncements(announcements.map(a =>
          a.id === editingAnn.id ? updated : a
        ));
      } else {
        const newAnn = await announcementsAPI.create(formData);
        setAnnouncements([newAnn, ...announcements]);
      }
      setShowCreateDialog(false);
    } catch (error) {
      console.error('Error saving announcement:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleRoleChange = async (memberId, newRole) => {
    setUpdatingRole(true);
    try {
      const updated = await directoryAPI.updateRole(memberId, newRole);
      setMembers(members.map(m => m.id === memberId ? updated : m));
      if (memberProfile && memberProfile.id === memberId) {
        setMemberProfile(updated);
      }
      setSelectedMember(null);
    } catch (error) {
      console.error('Error updating role:', error);
      alert('Failed to update role. Please try again.');
    } finally {
      setUpdatingRole(false);
    }
  };

  const handleViewMemberProfile = async (member) => {
    try {
      const fullProfile = await directoryAPI.getMemberFull(member.id);
      setMemberProfile(fullProfile);
      setShowMemberProfile(true);
    } catch (error) {
      console.error('Error fetching member profile:', error);
      alert('Failed to load member profile.');
    }
  };

  const handleStatusChange = async (isActive, reason = '') => {
    if (!memberProfile) return;
    
    setUpdatingStatus(true);
    try {
      const updated = await directoryAPI.updateStatus(memberProfile.id, isActive, reason);
      setMembers(members.map(m => m.id === memberProfile.id ? updated : m));
      setMemberProfile(updated);
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update member status. Please try again.');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getRoleBadge = (role) => {
    const roleStyles = {
      pastor: 'bg-tan-500 text-white',
      admin: 'bg-navy-900 text-white',
      deacon: 'bg-navy-700 text-white',
      minister: 'bg-navy-600 text-white',
      member: 'bg-gray-200 text-gray-700'
    };
    return roleStyles[role] || roleStyles.member;
  };

  const formatRole = (role) => {
    const roleLabels = {
      pastor: 'Pastor',
      admin: 'Admin',
      deacon: 'Deacon',
      minister: 'Minister',
      member: 'Member'
    };
    return roleLabels[role] || 'Member';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not provided';
    // If date is in YYYY-MM-DD format, parse it as local date
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateStr.split('-');
      return new Date(year, month - 1, day).toLocaleDateString('en-US', { 
        month: 'long', 
        day: 'numeric', 
        year: 'numeric' 
      });
    }
    // Handle ISO date strings
    if (dateStr.includes('T')) {
      const datePart = dateStr.split('T')[0];
      const [year, month, day] = datePart.split('-');
      return new Date(year, month - 1, day).toLocaleDateString('en-US', { 
        month: 'long', 
        day: 'numeric', 
        year: 'numeric' 
      });
    }
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getBackendUrl = () => {
    return process.env.REACT_APP_BACKEND_URL || '';
  };

  // Helper to construct proper avatar URL from various formats
  const getAvatarUrl = (avatar) => {
    if (!avatar) return null;
    // Handle Cloudinary URLs (new cloud storage format)
    if (avatar.includes('cloudinary.com') || avatar.includes('res.cloudinary.com')) {
      return avatar;
    }
    // Handle relative URLs (legacy local format)
    if (avatar.startsWith('/api/uploads/')) {
      return `${getBackendUrl()}${avatar}`;
    }
    // Handle legacy absolute URLs from different environments
    if (avatar.includes('/api/uploads/')) {
      const uploadPath = avatar.substring(avatar.indexOf('/api/uploads/'));
      return `${getBackendUrl()}${uploadPath}`;
    }
    // Fallback
    return avatar.startsWith('http') ? avatar : `${getBackendUrl()}${avatar}`;
  };

  // Filter members based on active status
  const filteredMembers = members.filter(m => {
    if (memberFilter === 'all') return true;
    if (memberFilter === 'active') return m.isActive !== false;
    if (memberFilter === 'inactive') return m.isActive === false;
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-navy-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-tan-400" />
      </div>
    );
  }

  const renderAnnouncementList = (list, isArchived = false) => (
    <div className="divide-y divide-gray-100">
      {list.map((ann) => (
        <div key={ann.id} className="p-4 hover:bg-gray-50 transition-colors">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                {ann.priority === 'high' && (
                  <Badge className="bg-tan-500 text-white text-xs">Important</Badge>
                )}
                {isArchived && (
                  <Badge variant="secondary" className="text-xs">Archived</Badge>
                )}
                {ann.imageUrl && <ImageIcon className="w-3 h-3 text-gray-400" />}
                {ann.videoUrl && <Video className="w-3 h-3 text-gray-400" />}
                <span className="text-xs text-gray-500">{formatDate(ann.createdAt)}</span>
              </div>
              <h4 className="font-bold text-navy-900 mb-1">{ann.title}</h4>
              <p className="text-sm text-gray-600 line-clamp-2">{ann.content}</p>
              {ann.imageUrl && (
                <div 
                  className="mt-2 relative cursor-pointer group inline-block"
                  onClick={() => setSelectedImage(ann.imageUrl)}
                >
                  <img src={ann.imageUrl} alt="" className="rounded-lg max-h-20 object-cover" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors rounded-lg flex items-center justify-center">
                    <ZoomIn className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
              )}
              <p className="text-xs text-gray-400 mt-2">— {ann.author}</p>
            </div>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-gray-500 hover:text-navy-900"
                onClick={() => handleEdit(ann)}
              >
                <Edit className="w-4 h-4" />
              </Button>
              {isArchived ? (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-gray-500 hover:text-green-600"
                  onClick={() => handleUnarchiveGlobal(ann.id)}
                  disabled={archiving === ann.id}
                >
                  {archiving === ann.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArchiveRestore className="w-4 h-4" />}
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-gray-500 hover:text-orange-600"
                  onClick={() => handleArchiveGlobal(ann.id)}
                  disabled={archiving === ann.id}
                >
                  {archiving === ann.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Archive className="w-4 h-4" />}
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-gray-500 hover:text-red-600"
                onClick={() => handleDelete(ann.id)}
                disabled={deleting === ann.id}
              >
                {deleting === ann.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>
      ))}
      {list.length === 0 && (
        <div className="p-8 text-center text-gray-500">
          <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>{isArchived ? 'No archived announcements' : 'No active announcements'}</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header with gradient */}
      <div className="bg-gradient-to-br from-navy-900 to-navy-800 text-white px-5 pt-8 pb-8 rounded-b-3xl shadow-xl">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center backdrop-blur-sm">
            <Shield className="w-6 h-6 text-tan-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Admin Panel</h1>
            <p className="text-tan-400 text-sm font-medium">Manage announcements & members</p>
          </div>
        </div>
      </div>

      <div className="px-4 -mt-4 space-y-4">
        {/* Main Tab Buttons */}
        <div className="flex bg-white rounded-xl p-1 shadow-xl">
          <button
            onClick={() => setMainTab('announcements')}
            className={`flex-1 flex items-center justify-center gap-1 py-3 px-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              mainTab === 'announcements'
                ? 'bg-navy-900 text-white shadow-md'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Bell className="w-4 h-4" />
            <span className="hidden sm:inline">Announcements</span>
            <span className="sm:hidden">News</span>
          </button>
          <button
            onClick={() => setMainTab('members')}
            className={`flex-1 flex items-center justify-center gap-1 py-3 px-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              mainTab === 'members'
                ? 'bg-navy-900 text-white shadow-md'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Users className="w-4 h-4" />
            Members
          </button>
          <button
            onClick={() => setMainTab('requests')}
            data-testid="admin-requests-tab"
            className={`flex-1 flex items-center justify-center gap-1 py-3 px-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              mainTab === 'requests'
                ? 'bg-navy-900 text-white shadow-md'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <ClipboardList className="w-4 h-4" />
            <span className="hidden sm:inline">Requests</span>
            <span className="sm:hidden">Reqs</span>
            {pendingRequests.length > 0 && (
              <Badge className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full ml-1">
                {pendingRequests.length}
              </Badge>
            )}
          </button>
          <button
            onClick={() => setMainTab('tools')}
            data-testid="admin-tools-tab"
            className={`flex-1 flex items-center justify-center gap-1 py-3 px-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              mainTab === 'tools'
                ? 'bg-navy-900 text-white shadow-md'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Wrench className="w-4 h-4" />
            Tools
          </button>
          <button
            onClick={() => setMainTab('manage')}
            data-testid="admin-manage-tab"
            className={`flex-1 flex items-center justify-center gap-1 py-3 px-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              mainTab === 'manage'
                ? 'bg-navy-900 text-white shadow-md'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <UserCog className="w-4 h-4" />
            Manage
          </button>
        </div>

        {mainTab === 'announcements' && (
          <>
            {/* Sub Tab Buttons */}
            <div className="flex bg-gray-200 rounded-lg p-1">
              <button
                onClick={() => setActiveTab('active')}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                  activeTab === 'active'
                    ? 'bg-white text-navy-900 shadow-sm'
                    : 'text-gray-500'
                }`}
              >
                Active ({activeAnnouncements.length})
              </button>
              <button
                onClick={() => setActiveTab('archived')}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                  activeTab === 'archived'
                    ? 'bg-white text-navy-900 shadow-sm'
                    : 'text-gray-500'
                }`}
              >
                Archived ({archivedAnnouncements.length})
              </button>
            </div>

            {/* Create Announcement Button */}
            <Button
              onClick={handleCreate}
              className="w-full h-12 bg-tan-500 hover:bg-tan-600 text-white rounded-xl font-semibold text-base shadow-md"
            >
              <Plus className="w-5 h-5 mr-2" />
              Create Announcement
            </Button>

            {/* Announcements List */}
            <Card className="border-0 shadow-lg rounded-2xl overflow-hidden">
              <CardContent className="p-0">
                <ScrollArea className="max-h-[calc(100vh-380px)]">
                  {activeTab === 'active'
                    ? renderAnnouncementList(activeAnnouncements, false)
                    : renderAnnouncementList(archivedAnnouncements, true)
                  }
                </ScrollArea>
              </CardContent>
            </Card>
          </>
        )}

        {mainTab === 'members' && (
          <>
            {/* Filter Tabs */}
            <div className="flex bg-gray-200 rounded-lg p-1">
              <button
                onClick={() => setMemberFilter('all')}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                  memberFilter === 'all' ? 'bg-white text-navy-900 shadow-sm' : 'text-gray-500'
                }`}
              >
                All ({members.length})
              </button>
              <button
                onClick={() => setMemberFilter('active')}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                  memberFilter === 'active' ? 'bg-white text-navy-900 shadow-sm' : 'text-gray-500'
                }`}
              >
                Active ({members.filter(m => m.isActive !== false).length})
              </button>
              <button
                onClick={() => setMemberFilter('inactive')}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                  memberFilter === 'inactive' ? 'bg-white text-navy-900 shadow-sm' : 'text-gray-500'
                }`}
              >
                Inactive ({members.filter(m => m.isActive === false).length})
              </button>
            </div>

            <Card className="border-0 shadow-lg rounded-2xl overflow-hidden">
              <CardContent className="p-0">
                <ScrollArea className="max-h-[calc(100vh-400px)]">
                  <div className="divide-y divide-gray-100">
                    {filteredMembers.map((member) => (
                      <div
                        key={member.id}
                        className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer ${member.isActive === false ? 'opacity-60' : ''}`}
                        onClick={() => handleViewMemberProfile(member)}
                      >
                        <div className="flex items-center gap-3">
                          {member.avatar ? (
                            <img 
                              src={getAvatarUrl(member.avatar)}
                              alt={`${member.firstName} ${member.lastName}`}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-navy-100 flex items-center justify-center">
                              <span className="text-navy-900 font-bold text-sm">
                                {member.firstName?.charAt(0)}{member.lastName?.charAt(0)}
                              </span>
                            </div>
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-bold text-navy-900">
                                {member.firstName} {member.lastName}
                              </p>
                              {member.isActive === false && (
                                <Badge className="bg-red-100 text-red-700 text-xs">Inactive</Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-500">{member.phone || 'No phone'}</p>
                          </div>
                          <Badge className={`${getRoleBadge(member.role)}`}>
                            {formatRole(member.role)}
                          </Badge>
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        </div>
                      </div>
                    ))}

                    {filteredMembers.length === 0 && (
                      <div className="p-8 text-center text-gray-500">
                        <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p>No members found</p>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </>
        )}

        {/* Pending Group Requests Tab */}
        {mainTab === 'requests' && (
          <>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-navy-900">Pending Group Requests</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchPendingRequests}
                disabled={loadingRequests}
                className="rounded-lg"
              >
                {loadingRequests ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Refresh'
                )}
              </Button>
            </div>

            <Card className="border-0 shadow-lg rounded-2xl overflow-hidden">
              <CardContent className="p-0">
                {loadingRequests ? (
                  <div className="p-8 text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-navy-600 mx-auto mb-2" />
                    <p className="text-gray-500">Loading requests...</p>
                  </div>
                ) : pendingRequests.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <ClipboardList className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="font-medium">No pending requests</p>
                    <p className="text-sm mt-1">All group join requests have been processed.</p>
                  </div>
                ) : (
                  <ScrollArea className="max-h-[calc(100vh-320px)]">
                    <div className="divide-y divide-gray-100">
                      {pendingRequests.map((request) => (
                        <div
                          key={`${request.groupId}-${request.userId}`}
                          data-testid="pending-request-item"
                          className="p-4 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <User className="w-4 h-4 text-gray-400" />
                                <p className="font-bold text-navy-900 truncate">{request.userName}</p>
                              </div>
                              {request.userEmail && (
                                <p className="text-sm text-gray-500 truncate">{request.userEmail}</p>
                              )}
                              <div className="flex items-center gap-2 mt-2">
                                <Badge className="bg-navy-100 text-navy-800 text-xs">
                                  {request.groupName}
                                </Badge>
                                <Badge variant="outline" className="text-xs flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {request.status === 'conversation_requested' ? 'Conversation' : 'Pending'}
                                </Badge>
                              </div>
                              {request.leaderName && (
                                <p className="text-xs text-gray-400 mt-1">Leader: {request.leaderName}</p>
                              )}
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                              <Button
                                variant="outline"
                                size="sm"
                                data-testid="deny-request-btn"
                                className="h-9 px-3 rounded-lg border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                                onClick={() => handleDenyRequest(request.groupId, request.userId)}
                                disabled={processingRequest === `${request.groupId}-${request.userId}`}
                              >
                                {processingRequest === `${request.groupId}-${request.userId}` ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <XCircle className="w-4 h-4 mr-1" />
                                    Deny
                                  </>
                                )}
                              </Button>
                              <Button
                                size="sm"
                                data-testid="approve-request-btn"
                                className="h-9 px-3 rounded-lg bg-green-600 hover:bg-green-700 text-white"
                                onClick={() => handleApproveRequest(request.groupId, request.userId)}
                                disabled={processingRequest === `${request.groupId}-${request.userId}`}
                              >
                                {processingRequest === `${request.groupId}-${request.userId}` ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <Check className="w-4 h-4 mr-1" />
                                    Approve
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Tools Tab */}
        {mainTab === 'manage' && (
          <>
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-navy-900">Manage</h3>
              <p className="text-sm text-gray-500">Reassign group leaders and clean up duplicate accounts</p>
            </div>

            {loadingManage ? (
              <div className="flex justify-center py-10">
                <Loader2 className="w-6 h-6 animate-spin text-navy-700" />
              </div>
            ) : (
              <>
                {/* Group Leaders */}
                <Card className="border-0 shadow-lg rounded-2xl overflow-hidden mb-4">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Crown className="w-5 h-5 text-amber-500" />
                      <h4 className="font-semibold text-navy-900">Group Leaders</h4>
                    </div>
                    <div className="space-y-4" data-testid="leader-manage-list">
                      {allGroups.map(group => (
                        <div key={group.id} className="border border-gray-100 rounded-xl p-3">
                          <p className="font-medium text-navy-900">{group.name}</p>
                          <p className="text-xs text-gray-500 mb-2">
                            Current leader: <span className="font-semibold">{group.leaderName || 'None'}</span>
                          </p>
                          <div className="flex gap-2">
                            <select
                              value={leaderSelections[group.id] || ''}
                              onChange={(e) => setLeaderSelections(prev => ({ ...prev, [group.id]: e.target.value }))}
                              className="flex-1 border border-gray-200 rounded-lg px-2 py-2 text-sm bg-white"
                              data-testid={`leader-select-${group.id}`}
                            >
                              <option value="">Choose a new leader…</option>
                              {allMembers.map(m => (
                                <option key={m.id} value={m.id}>
                                  {m.firstName} {m.lastName}{m.email ? ` (${m.email})` : ''}
                                </option>
                              ))}
                            </select>
                            <Button
                              size="sm"
                              onClick={() => handleSaveLeader(group.id)}
                              disabled={!leaderSelections[group.id] || savingLeader === group.id}
                              className="bg-navy-900 hover:bg-navy-800 text-white"
                              data-testid={`save-leader-${group.id}`}
                            >
                              {savingLeader === group.id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Set'}
                            </Button>
                          </div>
                        </div>
                      ))}
                      {allGroups.length === 0 && <p className="text-sm text-gray-400">No groups found.</p>}
                    </div>
                  </CardContent>
                </Card>

                {/* Duplicate Accounts */}
                <Card className="border-0 shadow-lg rounded-2xl overflow-hidden mb-4">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <GitMerge className="w-5 h-5 text-purple-500" />
                      <h4 className="font-semibold text-navy-900">Duplicate Accounts</h4>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">
                      Accounts sharing the same name. Choose the account to keep — the others are merged into it (nothing is lost) and the member is notified.
                    </p>
                    <div className="space-y-4" data-testid="duplicate-list">
                      {duplicateGroups.map(group => (
                        <div key={group.name} className="border border-purple-100 bg-purple-50/40 rounded-xl p-3" data-testid={`dup-group-${group.name.replace(/\s+/g, '-')}`}>
                          <p className="font-medium text-navy-900 mb-2">{group.name} <span className="text-xs text-gray-500">({group.accounts.length} accounts)</span></p>
                          <div className="space-y-2">
                            {group.accounts.map(acct => (
                              <label key={acct.id} className="flex items-start gap-2 bg-white rounded-lg p-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name={`dup-${group.name}`}
                                  checked={dupKeep[group.name] === acct.id}
                                  onChange={() => setDupKeep(prev => ({ ...prev, [group.name]: acct.id }))}
                                  className="mt-1"
                                  data-testid={`dup-keep-${acct.id}`}
                                />
                                <div className="flex-1 min-w-0 text-sm">
                                  <p className="text-gray-800">
                                    {acct.email || 'no email'} · {acct.phone || 'no phone'}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {acct.role} · {acct.activityCount} items of activity
                                  </p>
                                </div>
                              </label>
                            ))}
                          </div>
                          <Button
                            size="sm"
                            onClick={() => handleMergeDuplicates(group)}
                            disabled={!dupKeep[group.name] || merging === group.name}
                            className="mt-3 bg-purple-600 hover:bg-purple-700 text-white"
                            data-testid={`merge-btn-${group.name.replace(/\s+/g, '-')}`}
                          >
                            {merging === group.name ? <Loader2 className="w-4 h-4 animate-spin" /> : <><GitMerge className="w-4 h-4 mr-1" /> Merge into selected</>}
                          </Button>
                        </div>
                      ))}
                      {duplicateGroups.length === 0 && (
                        <p className="text-sm text-gray-400" data-testid="no-duplicates">No duplicate accounts found. 🎉</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </>
        )}


        {mainTab === 'tools' && (
          <>
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-navy-900">Admin Tools</h3>
              <p className="text-sm text-gray-500">Member registration and maintenance tools</p>
            </div>

            {/* Live Stream Toggle */}
            <Card className="border-0 shadow-lg rounded-2xl overflow-hidden mb-4">
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isLive ? 'bg-red-100' : 'bg-gray-100'}`}>
                      <span className={`w-3 h-3 rounded-full ${isLive ? 'bg-red-600 animate-pulse' : 'bg-gray-400'}`} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-navy-900">Live Stream {isLive && <span className="text-red-600 text-xs font-bold ml-1">● LIVE NOW</span>}</h4>
                      <p className="text-sm text-gray-500 mt-1">
                        Turn this on when service is streaming. Members will see a red "LIVE" badge on the Watch icon. Remember to turn it off when the stream ends.
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={isLive}
                    disabled={togglingLive}
                    onCheckedChange={handleToggleLive}
                    data-testid="livestream-toggle"
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg rounded-2xl overflow-hidden">
              <CardContent className="p-4 space-y-4">
                {/* Create New Member Tool */}
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-tan-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <UserPlus className="w-5 h-5 text-tan-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-navy-900">Register New Member</h4>
                      <p className="text-sm text-gray-500 mt-1">
                        Create an account for a new church member. You can set up their phone number and PIN so they can log in right away.
                      </p>
                      <Button
                        onClick={() => setShowCreateMemberDialog(true)}
                        className="mt-3 bg-tan-500 hover:bg-tan-600 text-white rounded-lg"
                        data-testid="create-member-btn"
                      >
                        <UserPlus className="w-4 h-4 mr-2" />
                        Register New Member
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Fix Leader Access Tool */}
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Users className="w-5 h-5 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-navy-900">Fix Leader Access</h4>
                      <p className="text-sm text-gray-500 mt-1">
                        If a group leader cannot access their group to approve requests, click this button to fix it. 
                        This ensures all leaders have proper access to their groups.
                      </p>
                      <Button
                        onClick={handleFixLeaderAccess}
                        disabled={fixingLeaders}
                        className="mt-3 bg-amber-500 hover:bg-amber-600 text-white rounded-lg"
                        data-testid="fix-leader-access-btn"
                      >
                        {fixingLeaders ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            Fixing...
                          </>
                        ) : (
                          <>
                            <Wrench className="w-4 h-4 mr-2" />
                            Fix Leader Access
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Birthday & Anniversary List Tool */}
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Gift className="w-5 h-5 text-pink-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-navy-900">Birthdays & Anniversaries</h4>
                      <p className="text-sm text-gray-500 mt-1">
                        View a list of member birthdays and wedding anniversaries organized by month.
                      </p>
                      <Button
                        onClick={loadBirthdayData}
                        disabled={loadingBirthdays}
                        className="mt-3 bg-pink-500 hover:bg-pink-600 text-white rounded-lg"
                        data-testid="birthday-list-btn"
                      >
                        {loadingBirthdays ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            Loading...
                          </>
                        ) : (
                          <>
                            <Calendar className="w-4 h-4 mr-2" />
                            View Birthdays & Anniversaries
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Send Celebration Notifications Tool */}
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Bell className="w-5 h-5 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-navy-900">Send Celebration Notifications</h4>
                      <p className="text-sm text-gray-500 mt-1">
                        Manually send birthday and anniversary push notifications to members celebrating today. Notifications are also sent automatically at 12:01 AM daily.
                      </p>
                      <Button
                        onClick={sendCelebrationNotifications}
                        disabled={sendingCelebrationNotifications}
                        className="mt-3 bg-amber-500 hover:bg-amber-600 text-white rounded-lg"
                        data-testid="send-celebration-notifications-btn"
                      >
                        {sendingCelebrationNotifications ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Cake className="w-4 h-4 mr-2" />
                            Send Today's Notifications
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Create/Edit Announcement Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-navy-900">{editingAnn ? 'Edit Announcement' : 'Create Announcement'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-navy-900">Title</Label>
              <Input
                placeholder="Announcement title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="h-11 bg-gray-50 border-gray-200 rounded-xl mt-1"
                required
              />
            </div>
            <div>
              <Label className="text-navy-900">Content</Label>
              <textarea
                placeholder="Write your announcement..."
                className="w-full min-h-[120px] px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-transparent mt-1"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                required
              />
            </div>
            <div>
              <Label className="text-navy-900">Priority</Label>
              <Select
                value={formData.priority}
                onValueChange={(value) => setFormData({ ...formData, priority: value })}
              >
                <SelectTrigger className="h-11 bg-gray-50 border-gray-200 rounded-xl mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">Important</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* File Upload */}
            <div className="space-y-2">
              <Label className="text-navy-900">Attach Image, Video, or Document (Flyer, PDF)</Label>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                className="w-full h-11 rounded-xl"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                {uploading ? 'Uploading...' : formData.imageUrl || formData.videoUrl ? 'Change File' : 'Upload Image, Video, or PDF'}
              </Button>

              {formData.imageUrl && (
                <div className="relative inline-block">
                  {formData.imageUrl.toLowerCase().endsWith('.pdf') ? (
                    <div className="flex items-center gap-2 p-3 bg-gray-100 rounded-xl">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <span className="text-sm font-medium text-gray-700">PDF Document Attached</span>
                    </div>
                  ) : (
                    <img 
                      src={formData.imageUrl} 
                      alt="Preview" 
                      className="rounded-xl max-h-32 object-cover"
                      onError={(e) => {
                        console.error('Image load error for:', formData.imageUrl);
                        e.target.style.display = 'none';
                      }}
                      onLoad={() => console.log('Image loaded successfully:', formData.imageUrl)}
                    />
                  )}
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute -top-2 -right-2 h-6 w-6 rounded-full"
                    onClick={() => setFormData({ ...formData, imageUrl: '' })}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              )}

              {formData.videoUrl && (
                <div className="relative inline-block">
                  <video src={formData.videoUrl} className="rounded-xl max-h-32" controls />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute -top-2 -right-2 h-6 w-6 rounded-full"
                    onClick={() => setFormData({ ...formData, videoUrl: '' })}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1 h-11 rounded-xl"
                onClick={() => setShowCreateDialog(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 h-11 bg-tan-500 hover:bg-tan-600 text-white rounded-xl"
                disabled={saving}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                  <><Save className="w-4 h-4 mr-2" />{editingAnn ? 'Update' : 'Create'}</>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Member Role Dialog */}
      <Dialog open={!!selectedMember} onOpenChange={() => setSelectedMember(null)}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-navy-900">Change Member Role</DialogTitle>
          </DialogHeader>
          {selectedMember && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                {selectedMember.avatar ? (
                  <img 
                    src={getAvatarUrl(selectedMember.avatar)}
                    alt={`${selectedMember.firstName} ${selectedMember.lastName}`}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-navy-100 flex items-center justify-center">
                    <User className="w-6 h-6 text-navy-900" />
                  </div>
                )}
                <div>
                  <p className="font-bold text-navy-900">
                    {selectedMember.firstName} {selectedMember.lastName}
                  </p>
                  <p className="text-sm text-gray-500">{selectedMember.email || selectedMember.phone}</p>
                </div>
              </div>

              <div>
                <Label className="text-navy-900 mb-2 block">Select Role</Label>
                <div className="grid grid-cols-2 gap-2">
                  {['member', 'deacon', 'minister', 'admin', 'pastor'].map((role) => (
                    <Button
                      key={role}
                      variant={selectedMember.role === role ? 'default' : 'outline'}
                      className={`h-11 rounded-xl capitalize ${
                        selectedMember.role === role ? 'bg-navy-900 text-white' : ''
                      }`}
                      onClick={() => handleRoleChange(selectedMember.id, role)}
                      disabled={updatingRole}
                    >
                      {updatingRole && selectedMember.role !== role ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : null}
                      {formatRole(role)}
                    </Button>
                  ))}
                </div>
              </div>

              <p className="text-xs text-gray-500 text-center">
                Click a role button to change this member&apos;s role
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Member Profile Dialog */}
      <Dialog open={showMemberProfile} onOpenChange={() => { setShowMemberProfile(false); setMemberProfile(null); }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-navy-900">Member Profile</DialogTitle>
          </DialogHeader>
          {memberProfile && (
            <div className="space-y-4">
              {/* Profile Header */}
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                {memberProfile.avatar ? (
                  <img 
                    src={getAvatarUrl(memberProfile.avatar)}
                    alt={`${memberProfile.firstName} ${memberProfile.lastName}`}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-navy-100 flex items-center justify-center">
                    <span className="text-navy-900 font-bold text-lg">
                      {memberProfile.firstName?.charAt(0)}{memberProfile.lastName?.charAt(0)}
                    </span>
                  </div>
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-bold text-navy-900 text-lg">
                      {memberProfile.firstName} {memberProfile.lastName}
                    </p>
                    {memberProfile.isActive === false && (
                      <Badge className="bg-red-100 text-red-700 text-xs">Inactive</Badge>
                    )}
                  </div>
                  <Badge className={`${getRoleBadge(memberProfile.role)} mt-1`}>
                    {formatRole(memberProfile.role)}
                  </Badge>
                </div>
              </div>

              {/* Contact Information */}
              <div className="space-y-3">
                <h4 className="font-semibold text-navy-900 text-sm">Contact Information</h4>
                <div className="space-y-2">
                  {memberProfile.email && (
                    <div className="flex items-center gap-3 text-sm">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-700">{memberProfile.email}</span>
                    </div>
                  )}
                  {memberProfile.phone && (
                    <div className="flex items-center gap-3 text-sm">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-700">{memberProfile.phone}</span>
                    </div>
                  )}
                  {memberProfile.mailingAddress && (
                    <div className="flex items-center gap-3 text-sm">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-700">{memberProfile.mailingAddress}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Personal Information */}
              <div className="space-y-3">
                <h4 className="font-semibold text-navy-900 text-sm">Personal Information</h4>
                <div className="grid grid-cols-2 gap-3">
                  {memberProfile.birthday && (
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <div>
                        <p className="text-xs text-gray-500">Birthday</p>
                        <p className="text-gray-700">{formatDate(memberProfile.birthday)}</p>
                      </div>
                    </div>
                  )}
                  {memberProfile.anniversary && (
                    <div className="flex items-center gap-2 text-sm">
                      <Heart className="w-4 h-4 text-gray-400" />
                      <div>
                        <p className="text-xs text-gray-500">Anniversary</p>
                        <p className="text-gray-700">{formatDate(memberProfile.anniversary)}</p>
                      </div>
                    </div>
                  )}
                  {memberProfile.spouseName && (
                    <div className="flex items-center gap-2 text-sm col-span-2">
                      <User className="w-4 h-4 text-gray-400" />
                      <div>
                        <p className="text-xs text-gray-500">Spouse</p>
                        <p className="text-gray-700">{memberProfile.spouseName}</p>
                      </div>
                    </div>
                  )}
                </div>
                {memberProfile.favoriteScripture && (
                  <div className="flex items-start gap-2 text-sm mt-2">
                    <BookOpen className="w-4 h-4 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-500">Favorite Scripture</p>
                      <p className="text-gray-700 italic">&ldquo;{memberProfile.favoriteScripture}&rdquo;</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Member Status Management */}
              <div className="space-y-3 pt-2 border-t">
                <h4 className="font-semibold text-navy-900 text-sm flex items-center gap-2">
                  <UserCog className="w-4 h-4" />
                  Member Status
                </h4>
                
                <div className="bg-gray-50 p-3 rounded-xl space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Active Member</span>
                    <Switch
                      checked={memberProfile.isActive !== false}
                      onCheckedChange={(checked) => {
                        if (!checked) {
                          // Will show reason selection
                        } else {
                          handleStatusChange(true, '');
                        }
                      }}
                      disabled={updatingStatus}
                    />
                  </div>
                  
                  {memberProfile.isActive === false && memberProfile.inactiveReason && (
                    <div className="text-sm">
                      <span className="text-gray-500">Reason: </span>
                      <span className="text-red-600 capitalize">{memberProfile.inactiveReason.replace(/_/g, ' ')}</span>
                    </div>
                  )}

                  {memberProfile.isActive !== false && (
                    <div className="space-y-2">
                      <p className="text-xs text-gray-500">Mark as inactive with reason:</p>
                      <div className="grid grid-cols-2 gap-2">
                        {['deceased', 'sick_n_shut_in', 'no_longer_member', 'unknown'].map((reason) => (
                          <Button
                            key={reason}
                            variant="outline"
                            size="sm"
                            className="text-xs h-8 rounded-lg capitalize"
                            onClick={() => handleStatusChange(false, reason)}
                            disabled={updatingStatus}
                          >
                            {updatingStatus ? (
                              <Loader2 className="w-3 h-3 animate-spin mr-1" />
                            ) : (
                              <UserX className="w-3 h-3 mr-1" />
                            )}
                            {reason.replace(/_/g, ' ')}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}

                  {memberProfile.isActive === false && (
                    <Button
                      variant="default"
                      size="sm"
                      className="w-full bg-green-600 hover:bg-green-700 text-white rounded-lg"
                      onClick={() => handleStatusChange(true, '')}
                      disabled={updatingStatus}
                    >
                      {updatingStatus ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <UserCheck className="w-4 h-4 mr-2" />
                      )}
                      Restore as Active Member
                    </Button>
                  )}
                </div>
              </div>

              {/* Role Change Section */}
              <div className="space-y-3 pt-2 border-t">
                <h4 className="font-semibold text-navy-900 text-sm">Change Role</h4>
                <div className="grid grid-cols-2 gap-2">
                  {['member', 'deacon', 'minister', 'admin', 'pastor'].map((role) => (
                    <Button
                      key={role}
                      variant={memberProfile.role === role ? 'default' : 'outline'}
                      size="sm"
                      className={`h-9 rounded-lg capitalize ${
                        memberProfile.role === role ? 'bg-navy-900 text-white' : ''
                      }`}
                      onClick={() => {
                        handleRoleChange(memberProfile.id, role);
                      }}
                      disabled={updatingRole || memberProfile.role === role}
                    >
                      {updatingRole ? (
                        <Loader2 className="w-3 h-3 animate-spin mr-1" />
                      ) : null}
                      {formatRole(role)}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Image Lightbox Dialog */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-4xl w-[95vw] p-0 bg-transparent border-0 shadow-none">
          <div className="relative">
            {/* Close button */}
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-12 right-0 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors z-50"
            >
              <X className="w-6 h-6 text-gray-800" />
            </button>
            
            {/* Full size image */}
            {selectedImage && (
              <img 
                src={selectedImage} 
                alt="Announcement"
                className="w-full h-auto max-h-[85vh] object-contain rounded-lg bg-white"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Member Dialog */}
      <Dialog open={showCreateMemberDialog} onOpenChange={setShowCreateMemberDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-navy-900 flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Register New Member
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateMember} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-navy-900">First Name *</Label>
                <Input
                  placeholder="First name"
                  value={newMemberData.firstName}
                  onChange={(e) => setNewMemberData({ ...newMemberData, firstName: e.target.value })}
                  className="h-11 bg-gray-50 border-gray-200 rounded-xl mt-1"
                  required
                  data-testid="create-member-firstname"
                />
              </div>
              <div>
                <Label className="text-navy-900">Last Name *</Label>
                <Input
                  placeholder="Last name"
                  value={newMemberData.lastName}
                  onChange={(e) => setNewMemberData({ ...newMemberData, lastName: e.target.value })}
                  className="h-11 bg-gray-50 border-gray-200 rounded-xl mt-1"
                  required
                  data-testid="create-member-lastname"
                />
              </div>
            </div>

            <div className="p-3 bg-navy-50 rounded-xl border border-navy-100">
              <p className="text-sm font-medium text-navy-900 mb-3">Phone + PIN Login (Recommended)</p>
              <div className="space-y-3">
                <div>
                  <Label className="text-navy-900">Phone Number *</Label>
                  <Input
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={newMemberData.phone}
                    onChange={(e) => setNewMemberData({ ...newMemberData, phone: e.target.value })}
                    className="h-11 bg-white border-gray-200 rounded-xl mt-1"
                    required
                    data-testid="create-member-phone"
                  />
                </div>
                <div>
                  <Label className="text-navy-900">PIN (4-6 digits) *</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="Create a 4-6 digit PIN"
                    value={newMemberData.pin}
                    onChange={(e) => setNewMemberData({ ...newMemberData, pin: e.target.value.replace(/\D/g, '') })}
                    className="h-11 bg-white border-gray-200 rounded-xl mt-1"
                    required
                    data-testid="create-member-pin"
                  />
                  <p className="text-xs text-gray-500 mt-1">Member will use this PIN to log in</p>
                </div>
              </div>
            </div>

            <div className="p-3 bg-gray-50 rounded-xl border border-gray-200">
              <p className="text-sm font-medium text-gray-600 mb-3">Email Login (Optional - being phased out)</p>
              <div className="space-y-3">
                <div>
                  <Label className="text-gray-600">Email</Label>
                  <Input
                    type="email"
                    placeholder="member@email.com"
                    value={newMemberData.email}
                    onChange={(e) => setNewMemberData({ ...newMemberData, email: e.target.value })}
                    className="h-11 bg-white border-gray-200 rounded-xl mt-1"
                    data-testid="create-member-email"
                  />
                </div>
                <div>
                  <Label className="text-gray-600">Password</Label>
                  <Input
                    type="text"
                    placeholder="Set a password (optional)"
                    value={newMemberData.password}
                    onChange={(e) => setNewMemberData({ ...newMemberData, password: e.target.value })}
                    className="h-11 bg-white border-gray-200 rounded-xl mt-1"
                    data-testid="create-member-password"
                  />
                </div>
              </div>
            </div>

            <div>
              <Label className="text-navy-900">Role</Label>
              <Select
                value={newMemberData.role}
                onValueChange={(value) => setNewMemberData({ ...newMemberData, role: value })}
              >
                <SelectTrigger className="h-11 bg-gray-50 border-gray-200 rounded-xl mt-1" data-testid="create-member-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="deacon">Deacon</SelectItem>
                  <SelectItem value="minister">Minister</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="pastor">Pastor</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-navy-900">Birthday</Label>
              <Input
                type="date"
                value={newMemberData.birthday}
                onChange={(e) => setNewMemberData({ ...newMemberData, birthday: e.target.value })}
                className="h-11 bg-gray-50 border-gray-200 rounded-xl mt-1"
              />
            </div>

            <div>
              <Label className="text-navy-900">Address</Label>
              <Input
                placeholder="123 Main St, City, State"
                value={newMemberData.address}
                onChange={(e) => setNewMemberData({ ...newMemberData, address: e.target.value })}
                className="h-11 bg-gray-50 border-gray-200 rounded-xl mt-1"
              />
            </div>

            <div>
              <Label className="text-navy-900">Favorite Scripture</Label>
              <Input
                placeholder="John 3:16"
                value={newMemberData.favoriteScripture}
                onChange={(e) => setNewMemberData({ ...newMemberData, favoriteScripture: e.target.value })}
                className="h-11 bg-gray-50 border-gray-200 rounded-xl mt-1"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1 h-11 rounded-xl"
                onClick={() => setShowCreateMemberDialog(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 h-11 bg-tan-500 hover:bg-tan-600 text-white rounded-xl"
                disabled={creatingMember}
                data-testid="submit-create-member"
              >
                {creatingMember ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Register Member
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Birthday & Anniversary List Dialog */}
      <Dialog open={showBirthdayList} onOpenChange={setShowBirthdayList}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-hidden rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-navy-900 flex items-center gap-2">
              <Gift className="w-5 h-5 text-pink-500" />
              Birthdays & Anniversaries
            </DialogTitle>
          </DialogHeader>
          
          {birthdayData && (
            <div className="space-y-4">
              {/* Month Selector */}
              <div className="flex gap-2 overflow-x-auto pb-2">
                {birthdayData.months?.map((month) => {
                  const hasBirthdays = birthdayData.birthdays[month]?.length > 0;
                  const hasAnniversaries = birthdayData.anniversaries[month]?.length > 0;
                  const hasData = hasBirthdays || hasAnniversaries;
                  
                  return (
                    <button
                      key={month}
                      onClick={() => setSelectedMonth(month)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                        selectedMonth === month
                          ? 'bg-pink-500 text-white'
                          : hasData
                            ? 'bg-pink-100 text-pink-700 hover:bg-pink-200'
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                    >
                      {month.slice(0, 3)}
                      {hasData && selectedMonth !== month && (
                        <span className="ml-1 text-xs">•</span>
                      )}
                    </button>
                  );
                })}
              </div>
              
              {/* Content for Selected Month */}
              <ScrollArea className="h-[50vh]">
                <div className="space-y-4 pr-4">
                  {/* Birthdays */}
                  <div>
                    <h3 className="font-semibold text-navy-900 flex items-center gap-2 mb-3">
                      <Cake className="w-4 h-4 text-pink-500" />
                      Birthdays in {selectedMonth}
                    </h3>
                    {birthdayData.birthdays[selectedMonth]?.length > 0 ? (
                      <div className="space-y-2">
                        {birthdayData.birthdays[selectedMonth].map((member) => (
                          <div key={member.id} className="flex items-center gap-3 p-3 bg-pink-50 rounded-xl">
                            <div className="w-10 h-10 rounded-full bg-pink-200 flex items-center justify-center text-pink-700 font-semibold text-sm overflow-hidden">
                              {member.avatar ? (
                                <img src={member.avatar.startsWith('http') ? member.avatar : `${process.env.REACT_APP_BACKEND_URL}${member.avatar}`} alt="" className="w-full h-full object-cover" />
                              ) : (
                                member.name.split(' ').map(n => n[0]).join('')
                              )}
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-navy-900">{member.name}</p>
                              <p className="text-sm text-gray-500">{formatDate(member.date)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 italic">No birthdays in {selectedMonth}</p>
                    )}
                  </div>
                  
                  {/* Anniversaries */}
                  <div>
                    <h3 className="font-semibold text-navy-900 flex items-center gap-2 mb-3">
                      <Heart className="w-4 h-4 text-red-500" />
                      Wedding Anniversaries in {selectedMonth}
                    </h3>
                    {birthdayData.anniversaries[selectedMonth]?.length > 0 ? (
                      <div className="space-y-2">
                        {birthdayData.anniversaries[selectedMonth].map((member) => (
                          <div key={member.id} className="flex items-center gap-3 p-3 bg-red-50 rounded-xl">
                            <div className="w-10 h-10 rounded-full bg-red-200 flex items-center justify-center text-red-700 font-semibold text-sm overflow-hidden">
                              {member.avatar ? (
                                <img src={member.avatar.startsWith('http') ? member.avatar : `${process.env.REACT_APP_BACKEND_URL}${member.avatar}`} alt="" className="w-full h-full object-cover" />
                              ) : (
                                member.name.split(' ').map(n => n[0]).join('')
                              )}
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-navy-900">{member.name}</p>
                              <p className="text-sm text-gray-500">{formatDate(member.date)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 italic">No anniversaries in {selectedMonth}</p>
                    )}
                  </div>
                </div>
              </ScrollArea>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <BottomNav />
    </div>
  );
};

export default AdminPage;
